import React from "react";
import { Box, Typography, Table, TableBody, TableRow, TableCell, Paper, Button } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { PrintHeader, PrintHeaderSpacer, PrintFooter, PrintFooterSpacer } from "../../utils/PrintHeader";

// Les étapes arrivent en français depuis deriveRanking() (Brackets.jsx) —
// traduites ici uniquement pour l'affichage de CE document (le reste de
// l'appli reste en français pour l'équipe de chronométrage).
const STAGE_TRANSLATIONS = {
    "Finale": "Final",
    "Petite finale": "Small final",
    "Demi-finales": "Semi-finals",
    "Quarts de finale": "1/4 Final",
    "1/8 de finale": "1/8 Final",
    "1/16 de finale": "1/16 Final",
};

function translateStage(stage) {
    return STAGE_TRANSLATIONS[stage] || stage;
}

// Classement stylisé, groupé par étape (Final, Small final, 1/4, 1/8...),
// avec un en-tête de section par étape — même esprit que le "Official
// ranking" FIS (rangs ex-æquo groupés sous un même titre de section).
export default function RankingTable({ ranking, title, branding }) {
    if (!ranking || ranking.length === 0) return null;

    // Regroupe les entrées consécutives par étape (déjà triées par place
    // croissante dans deriveRanking, donc un simple regroupement séquentiel
    // suffit).
    const groups = [];
    let currentStage = null;
    ranking.forEach((entry) => {
        if (entry.stage !== currentStage) {
            groups.push({ stage: entry.stage, entries: [] });
            currentStage = entry.stage;
        }
        groups[groups.length - 1].entries.push(entry);
    });

    return (
        <Paper
            sx={{
                p: 3,
                mb: 3,
                "@media print": {
                    boxShadow: "none",
                    border: "none",
                    p: 0,
                    px: "10mm",
                    mb: 0,
                },
            }}
        >
            {/* Marge à 0 : le bandeau et le pied de page (position fixed
                dans PrintHeader/PrintFooter) doivent toucher les bords de
                la feuille. La marge horizontale du contenu est gérée via
                le padding du Paper ci-dessus, et la marge verticale via
                PrintHeaderSpacer/PrintFooterSpacer plus bas. */}
            <style>{`
                @media print {
                    @page {
                        margin: 0;
                    }
                }
            `}</style>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: title ? -1 : 1,
                    "@media print": { display: "none" },
                }}
            >
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                    Print
                </Button>
            </Box>

            <PrintHeader branding={branding} />
            <PrintHeaderSpacer branding={branding} />

            {title && (
                <Typography variant="h5" align="center" sx={{ mb: 2, fontWeight: "bold" }}>
                    {title}
                </Typography>
            )}

            {/* Chaque colonne (hormis Name) a maintenant une largeur fixe
                généreuse — sans ça, la moindre largeur "en trop" (table à
                100%) se retrouvait entièrement absorbée par la colonne
                Name (texte non coupé, donc juste plus d'espace vide),
                créant l'écart disgracieux vu précédemment. En donnant à
                CHAQUE colonne sa propre largeur, l'espace se répartit
                across toutes les colonnes au lieu de se concentrer en un
                seul endroit. */}
            <Table size="small" sx={{ width: "100%" }}>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", border: 0, width: 70 }}>Rank</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: 0, width: 90 }}>Bib</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: 0, width: 130 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: 0 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: 0, width: 100, pl: "5px" }}>
                            Year
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", border: 0, width: 130 }}>Nation</TableCell>
                    </TableRow>

                    {groups.map((group, gi) => (
                        <React.Fragment key={gi}>
                            <TableRow>
                                <TableCell colSpan={6} sx={{ border: 0, pt: 2.5, pb: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                        {translateStage(group.stage)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                            {group.entries.map((entry, i) => (
                                <TableRow
                                    key={entry.fiscode || `${gi}-${i}`}
                                    sx={{
                                        backgroundColor: i % 2 === 0 ? "#f5f5f5" : "transparent",
                                        // Sans ça, la plupart des navigateurs omettent les
                                        // couleurs de fond à l'impression par défaut.
                                        printColorAdjust: "exact",
                                        WebkitPrintColorAdjust: "exact",
                                    }}
                                >
                                    <TableCell sx={{ border: 0, fontWeight: "bold", width: 70 }}>
                                        {entry.place}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, width: 90 }}>{entry.bib}</TableCell>
                                    <TableCell sx={{ border: 0, width: 130 }}>{entry.fiscode}</TableCell>
                                    <TableCell sx={{ border: 0, fontWeight: "bold", whiteSpace: "nowrap" }}>
                                        {entry.name}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, width: 100, pl: "5px" }}>
                                        {entry.birthyear}
                                    </TableCell>
                                    <TableCell sx={{ border: 0, width: 130 }}>{entry.nation}</TableCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>

            <PrintFooterSpacer branding={branding} />
            <PrintFooter branding={branding} />
        </Paper>
    );
}